import { createMemo, For } from 'solid-js'

const LAYERS = 6

/**
 * Progressive (gradient) backdrop blur. CSS has no native progressive blur, so
 * fake it by stacking `LAYERS` backdrop-filter layers, each masked to a band
 * and each with a different (doubling) blur radius. Blur ramps from ~0 up to
 * `maxBlur` toward the `strong` edge; the feathered, overlapping band masks
 * blend adjacent radii into a smooth gradient.
 *
 * Render as the first child of a positioned (relative/fixed) container, with
 * the real content in a sibling that sits above it (e.g. `relative z-10`).
 */
export function ProgressiveBlur(props: {
  strong: 'top' | 'bottom'
  maxBlur: number
}) {
  const layers = createMemo(() => {
    const dir = props.strong === 'top' ? 'to top' : 'to bottom'
    const maxBlur = props.maxBlur
    const step = 100 / LAYERS
    return Array.from({ length: LAYERS }, (_, idx) => {
      const blur = maxBlur * 2 ** (idx - (LAYERS - 1))
      const from = Math.max((idx - 1) * step, 0)
      const a = idx * step
      const b = (idx + 1) * step
      const to = Math.min((idx + 2) * step, 100)
      const mask =
        idx === LAYERS - 1
          ? `linear-gradient(${dir}, transparent ${from}%, #000 ${a}%, #000 100%)`
          : `linear-gradient(${dir}, transparent ${from}%, #000 ${a}%, #000 ${b}%, transparent ${to}%)`
      return { blur, mask }
    })
  })

  return (
    <div
      class="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden="true"
    >
      <For each={layers()}>
        {(layer) => (
          <div
            class="absolute inset-0"
            style={{
              'backdrop-filter': `blur(${layer.blur}px)`,
              '-webkit-backdrop-filter': `blur(${layer.blur}px)`,
              'mask-image': layer.mask,
              '-webkit-mask-image': layer.mask,
            }}
          />
        )}
      </For>
    </div>
  )
}
